import { FormParagraph } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { LabResult } from '@frontend/components/interfaces';
import type { ProfessionalSummaryDataSectionLoadResult } from '@frontend/utils/professionalSummaryDataSectionLoadResult';
import { ProfessionalSummaryDataType } from '@common/enums/professionalSummaryDataType';
import { TableBody, TableData, TableHead, TableHeader, TableRow } from '@frontend/components/elements/common';
import { ProfessionalSummaryDataSection } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataSection';
import { ProfessionalSummaryDataTable } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataTable';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks';

interface ProcessActivityLabResultsReadProps {
    processActivityId: string;
    showParent: () => void;
}
export function ProcessActivityLabResultsRead(props: ProcessActivityLabResultsReadProps) {
    const { processActivityId, showParent } = props;
    const language = getSelectedLanguage();

    const abortableFetch = useAbortableFetch();
    const [isSectionOpen, setIsSectionOpen] = useState(false);
    const [labResults, setLabResults] = useState<LabResult[]>();
    const [selectedLabResultsCount, setSelectedLabResultsCount] = useState<number>(0);
    const [labResultsLoadResult, setLabResultsLoadResult] = useState<ProfessionalSummaryDataSectionLoadResult>();

    useEffect(() => {
        loadLabResults();
    }, []);

    if (selectedLabResultsCount == 0) {
        return null;
    }

    return (
        <ProfessionalSummaryDataSection
            isOpen={isSectionOpen}
            loadResult={labResultsLoadResult}
            onToggle={() => setIsSectionOpen(!isSectionOpen)}
            professionalSummaryDataType={ProfessionalSummaryDataType.diagnosticDeterminations}
            readOnly
            selectionCount={selectedLabResultsCount}
        >
            {labResults && labResults.length > 0 && (
                <ProfessionalSummaryDataTable isRead={true}>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{language.labResults.description}</TableHeader>
                            <TableHeader>{language.labResults.valueValue}</TableHeader>
                            <TableHeader>{language.labResults.valueUnit}</TableHeader>
                            <TableHeader>{language.common.date}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {labResults.map((labResult) => {
                            return (
                                <TableRow key={labResult.id}>
                                    <TableData>{labResult.codeDisplayName}</TableData>
                                    <TableData>{labResult.valueValue}</TableData>
                                    <TableData>{labResult.valueUnit}</TableData>
                                    <TableData>
                                        <FormParagraph>{labResult.startDate}</FormParagraph>
                                    </TableData>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ProfessionalSummaryDataTable>
            )}
        </ProfessionalSummaryDataSection>
    );

    async function loadLabResults() {
        let isLoadCompleted = false;
        let loadFailed = false;

        try {
            const results = await abortableFetch.fetchJson(
                `/api/processActivities/${processActivityId}/labResults`,
                'GET'
            );

            if (results.json.total > 0) {
                showParent();
            }

            setLabResults(results.json.items);
            setSelectedLabResultsCount(results.json.total);
        } catch (error) {
            loadFailed = true;
        }

        isLoadCompleted = true;
        setLabResultsLoadResult({ isLoadCompleted, loadFailed });
    }
}
